import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { DeviceStatus, DriftDevice, DriftStatus } from "../api/types";
import { DeviceStatusPill } from "./DeviceStatusPill";
import { DriftBadge } from "./DriftBadge";

interface TenantDevicesViewProps {
  tenantSlug: string;
  tenantDisplayName: string;
  onBack: () => void;
  onDeviceClick?: (device: DriftDevice) => void;
}

const STATUS_FILTERS: { value: "all" | DeviceStatus; label: string }[] = [
  { value: "all", label: "All status" },
  { value: "alive", label: "Alive" },
  { value: "unreachable", label: "Unreachable" },
  { value: "no-creds", label: "No creds" },
  { value: "error", label: "Error" },
  { value: "unknown", label: "Unknown" },
];

const DRIFT_FILTERS: { value: "all" | DriftStatus; label: string }[] = [
  { value: "all", label: "All drift" },
  { value: "aligned", label: "Aligned" },
  { value: "intent_only", label: "Intent only" },
  { value: "reality_only", label: "Rogue" },
];

export function TenantDevicesView({
  tenantSlug,
  tenantDisplayName,
  onBack,
  onDeviceClick,
}: TenantDevicesViewProps) {
  const [statusFilter, setStatusFilter] = useState<"all" | DeviceStatus>("all");
  const [driftFilter, setDriftFilter] = useState<"all" | DriftStatus>("all");
  const [classFilter, setClassFilter] = useState<string>("all");

  const devicesQuery = useQuery({
    queryKey: ["tenant-devices", tenantSlug],
    queryFn: () => api.tenantDevices(tenantSlug),
    refetchInterval: 30_000,
  });

  const classes = useMemo(
    () => Array.from(new Set((devicesQuery.data?.devices ?? []).map((d) => d.device_class))).sort(),
    [devicesQuery.data],
  );

  const filtered = useMemo(() => {
    return (devicesQuery.data?.devices ?? []).filter((d) => {
      if (statusFilter !== "all" && d.status !== statusFilter) return false;
      if (driftFilter !== "all" && d.drift_status !== driftFilter) return false;
      if (classFilter !== "all" && d.device_class !== classFilter) return false;
      return true;
    });
  }, [devicesQuery.data, statusFilter, driftFilter, classFilter]);

  return (
    <div>
      <button onClick={onBack} style={backButtonStyle}>
        ← Fleet
      </button>
      <h2 style={{ margin: "16px 0 4px", fontSize: "1.4rem" }}>{tenantDisplayName}</h2>
      <p style={{ color: "var(--text-dim)", margin: 0, fontSize: "0.9rem" }}>
        Devices, live status, and intent-vs-reality drift. Polled every 30 s.
      </p>

      {devicesQuery.data && <DriftSummaryCards drift={devicesQuery.data.drift} />}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, margin: "20px 0" }}>
        <FilterGroup
          label="Status"
          value={statusFilter}
          options={STATUS_FILTERS}
          onChange={(v) => setStatusFilter(v as typeof statusFilter)}
        />
        <FilterGroup
          label="Drift"
          value={driftFilter}
          options={DRIFT_FILTERS}
          onChange={(v) => setDriftFilter(v as typeof driftFilter)}
        />
        {classes.length > 1 && (
          <FilterGroup
            label="Class"
            value={classFilter}
            options={[
              { value: "all", label: "All classes" },
              ...classes.map((c) => ({ value: c, label: c })),
            ]}
            onChange={setClassFilter}
          />
        )}
      </div>

      {devicesQuery.isLoading && <p style={{ color: "var(--text-dim)" }}>Loading devices…</p>}
      {devicesQuery.error && (
        <p style={{ color: "var(--crit)" }}>
          Failed to load devices: {String(devicesQuery.error)}
        </p>
      )}
      {devicesQuery.data && filtered.length === 0 && (
        <p style={{ color: "var(--text-dim)" }}>
          No devices match the current filter. Try clearing the filter or check that the
          collector has run for this tenant.
        </p>
      )}
      {filtered.length > 0 && (
        <DeviceTable devices={filtered} onDeviceClick={onDeviceClick} />
      )}
    </div>
  );
}

function DriftSummaryCards({
  drift,
}: {
  drift: { aligned: number; intent_only: number; reality_only: number; total: number };
}) {
  const cards = [
    { label: "Total", value: drift.total, fg: "var(--text)" },
    { label: "Aligned", value: drift.aligned, fg: "var(--ok)" },
    { label: "Intent only", value: drift.intent_only, fg: "var(--warn)" },
    { label: "Rogue", value: drift.reality_only, fg: "var(--accent)" },
  ];
  return (
    <div
      style={{
        marginTop: 20,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 12,
      }}
    >
      {cards.map((c) => (
        <div
          key={c.label}
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "12px 16px",
          }}
        >
          <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>
            {c.label}
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 600, color: c.fg, marginTop: 4 }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

interface FilterGroupProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
}

function FilterGroup({ label, value, options, onChange }: FilterGroupProps) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: "0.75rem", color: "var(--text-dim)", textTransform: "uppercase" }}>
        {label}
      </span>
      <div style={{ display: "flex", gap: 4 }}>
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              style={{
                background: active ? "var(--accent)" : "transparent",
                color: active ? "var(--bg)" : "var(--text)",
                border: "1px solid var(--border)",
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: "0.78rem",
                fontWeight: active ? 600 : 400,
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DeviceTable({
  devices,
  onDeviceClick,
}: {
  devices: DriftDevice[];
  onDeviceClick?: (device: DriftDevice) => void;
}) {
  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
        <thead>
          <tr style={{ background: "var(--surface-2)", textAlign: "left" }}>
            <Th>Status</Th>
            <Th>Drift</Th>
            <Th>Label</Th>
            <Th>Class</Th>
            <Th>External ID</Th>
            <Th>Last seen</Th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => {
            const label = d.intent_label || d.reality_label || "(unnamed)";
            const sublabel =
              d.area_label || (d.canonical_slug && d.canonical_slug !== label ? d.canonical_slug : "");
            const clickable = onDeviceClick && d.device_id !== null;
            return (
              <tr
                key={`${d.device_class}:${d.external_id}`}
                style={{
                  borderTop: "1px solid var(--border)",
                  cursor: clickable ? "pointer" : "default",
                }}
                onClick={clickable ? () => onDeviceClick!(d) : undefined}
              >
                <Td>
                  <DeviceStatusPill status={d.status} />
                </Td>
                <Td>
                  <DriftBadge drift={d.drift_status} compact />
                </Td>
                <Td>
                  <div>{label}</div>
                  {sublabel && (
                    <div style={{ color: "var(--text-dim)", fontSize: "0.72rem", marginTop: 2 }}>
                      {sublabel}
                    </div>
                  )}
                </Td>
                <Td>{d.device_class}</Td>
                <Td>
                  <code style={{ color: "var(--text-dim)", fontSize: "0.75rem" }}>
                    {d.external_id}
                  </code>
                </Td>
                <Td>
                  <span style={{ color: "var(--text-dim)", fontSize: "0.78rem" }}>
                    {d.last_observation_at
                      ? new Date(d.last_observation_at).toLocaleString()
                      : "—"}
                  </span>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: "10px 14px" }}>{children}</td>;
}

const backButtonStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--accent)",
  border: "none",
  padding: "4px 0",
  fontSize: "0.85rem",
  cursor: "pointer",
};
